export interface BillingDataResponse {
    data: BillingData;
    /** Суммарная информация по всем услугам пользователя в Биллинге */
    services: {
        /** Услуги, которые не относятся к конкретному сервису */
        common?: BillingDataServicesDisableAds & BillingDataCommonServicesFamily;
        /** Услуги, которые относятся к Облаку */
        cloud?: BillingDataServicesBaseQuota &
            BillingDataServicesQuota &
            BillingDataServicesGifts &
            BillingDataFreeUpSpace &
            BillingDataCloudServicesFamily & {
                /** Флаги, выставляемые в UDB */
                flags: BillingDataServicesPaidFlags;
            };
        /**  Услуги, которые относятся к Почте */
        mail?: BillingDataServicesMail;
        /** Услуги, которые относятся к Заметкам */
        notes?: BillingDataServicesNotes;
    };
    paid_info: BillingPaidInfo;
    user?: {
        exists: boolean;
    };
}

export interface BillingDataHasProduct {
    // ID тарифа
    product: string;
}

export interface BillingDataWebGraceHistory {
    // дата списания
    date: number;
    // сколько списали
    amount: number;
    // идентификатор списания
    pid: string;
}

export interface BillingDataWebGraceInterval {
    // дата продления
    date: number;
    // На какой период продлили
    last_interval: string;
    // На какой период осталось продлить
    future_interval: string;
}

interface BillingDataWebGrace {
    // Активен ли грейс период (находится ли подписка в периоде 21 дня списаний)
    active?: boolean;
    // Информация по частичному грейсу в 21 день для месячных подписок
    partial?: {
        // Активны ли попытки списания
        active: boolean;
        // На какое кол-во шагов текущий частичный грейс
        steps: number;
        // Сколько уже списали в рамках текущего грейса
        cummulative_total: number;
        // Сколько осталось списать
        remainder: number;
        // История списаний в рамках текущего грейса { date = ..., amount = ..., pid = ... }
        history: BillingDataWebGraceHistory[];
    };
    // Информация по длительно-частичным грейсам (для годовых подписок)
    partial_ll?: {
        active: boolean;
        // Сколько уже списали в рамках всех грейсов
        cummulative_total: number;
        // Сколько осталось списать
        remainder: number;
        // На какой период продлили
        last_interval: string;
        // На какой период осталось продлить
        future_interval: string;
        // История всех списаний в рамках всех грейсов { date = ..., amount = ..., pid = ... }
        history: BillingDataWebGraceHistory[];
        // Интервалы продлений
        intervals: BillingDataWebGraceInterval[];
    };
}

interface BillingDataServicesBaseQuota {
    /** Базовая квота пользователя */
    basequota?: number;
}

export interface BillingDataServicesQuota {
    /** Добавляемая квота байтах */
    quota: number;
}

export interface BillingDataServicesGifts {
    /** ID подарков, выдаваемых при покупке определенных тарифов */
    gifts?: string[];
}

export interface BillingDataFreeUpSpace {
    /** Пользователю доступны фичи очистки места (тиндерсвайп, удаление дубликатов) */
    free_up_space?: boolean;
}

export interface BillingDataServicesDisableAds {
    /** Отключена ли реклама в Почте/Облаке */
    disable_ads: boolean;
}
export interface BillingDataCommonServicesFamily {
    /** Доступно создание семейной подписки; */
    family?: boolean;
}
export interface BillingDataServicesMail {
    /** Может добавлять марки к письмам в почте */
    labels: boolean;
}

export interface BillingDataServicesUnlocked {
    // Тарифы, которые пользователь получит при активации партнерской подписки (Если есть данное поле, то остальных полей в cloud не будет)
    unlocked?: {
        // ID веб тарифа
        web: string;
    };
}
export interface BillingDataServicesNotes {
    /** Может использовать AI помощника в Заметках */
    mb_ai_assistant: boolean;
}

export interface BillingDataServicesPaidFlags {
    /** Дает аплоад в 32 гб */
    PAID_UPLOAD: boolean;
    /** Выключает рекламу */
    PAID_ACCOUNT: boolean;
    /** Безлимитная автозагрузка */
    UNLIM_UPL?: boolean;
}

interface BillingDataServicesProFlags {
    /** Дает аплоад в 100 ГБ */
    PRO?: boolean;
}

export interface BillingDataCloudServicesFamily {
    family?: {
        /* Доступны семейные паблики */
        folders: boolean;
        /* Доступны семейные альбомы */
        albums: boolean;
    };
}

export interface BillingDataStartFinishCreate {
    // Время начала действия подписки в формате timestamp
    start?: number;
    // Дата окончания подписки в формате timestamp
    finish?: number;
    // Дата создания подарка
    create?: number;
}

export const BILLING_DATA_ALL_PERIOD = 'past,future,current';
export type BillingDataAtomicPeriods = 'past' | 'current' | 'future';
export type BillingDataPeriods = BillingDataAtomicPeriods | typeof BILLING_DATA_ALL_PERIOD;

export interface BillingDataArgs {
    mode?: 'paid_info';
    period?: BillingDataPeriods;
}

export enum BillingDataPlatform {
    web = 'web',
    ios = 'ios',
    android = 'android',
    reward = 'reward',
    base = 'base',
    huawei = 'huawei',
    rustore = 'rustore',
}

export enum BillingDataPartner {
    disco = 'disco',
    rostelecom = 'rostelecom',
    mail = 'ru.mail.special',
    tele2 = 'tele2',
    megafon = 'megafon',
    sferum = 'sferum',
    vkmusic = 'vkmusic',
    tbank = 'tbank',
}

export enum BillingDataStatus {
    ACTIVE = 'A',
    FINISHED = 'F',
    SUSPENDED = 'S',
    GRACE = 'G',
    HOLD = 'H', // Пока нет
    LAPSED = 'L',
    WAITING = 'W',
}

export enum BillingDataType {
    TRIAL = 'trial',
    PAID = 'paid',
    DISCOUNT = 'discount',
    PREPAID = 'prepaid',
    FREE = 'free',
    FREE_START = 'free_start',
}

export enum BillingDataPeriod {
    DISCOUNT = 'discount',
    TRIAL = 'trial',
    REGULAR = 'regular',
    FREE_START = 'free_start',
}

/** Подписки по платформам, подарки, промопериоды и прочее */
export interface BillingData {
    web: BillingDataWeb[];
    ios: BillingDataIos[];
    android: BillingDataReward[];
    huawei: BillingDataReward[];
    reward: BillingDataReward[];
    rustore: BillingDataReward[];
    promo: BillingDataPromo[];
    bonus: BillingDataBonus[];
    gift_received: BillingDataGiftReceived[];
    gift_sent: BillingDataGiftSender[];
    // Подписки в сторонних сервисах (На данный момент черновой вариант, нереализованный в Биллинге)
    purchase?: Partial<Record<BillingDataPlatform, Record<string, BillingDataPurchase>>>;
    referral: BillingDataReferralProgramResponse[];
}

export interface BillingDataWebAmounts {
    // Цена покупки подписки (для подписки со скидкой тут будет указана скидочная цена, для подписки с триалом будет 0)
    purchase: number;
    // Цена продления подписки
    renewal: number;
    // Стандартная цена подписки
    regular: number;
    // [Опционально] цена скидочного периода, если он есть
    discount?: number;
}

// Длины периодов подписки
export interface BillingDataWebIntervals {
    // Длина основного периода подписки
    paid: string;
    // [Опционально] длина скидочного периода подписки
    discount?: string;
    // [Опционально] длина триального периода подписки
    trial?: string;
    // [Опционально] длина семейного фримиума
    free_start?: string;
}

export interface BillingDataWeb extends BillingDataHasProduct {
    // ID подписки
    sub_id: string;
    // Время начала действия подписки в формате timestamp
    start: number;
    // Дата окончания подписки в формате timestamp
    finish: number;
    // Дата следущего продления в формате timestamp
    renew_next: number;
    // Продлится ли подписка самостоятельно
    autorenewable: boolean;
    // Можно ли обновить подписку вручную
    renewable?: boolean;
    // Цены подписки
    amounts: BillingDataWebAmounts;
    // Длины периодов подписки
    intervals: BillingDataWebIntervals;
    // Список типов подписки. Доступные значения: paid, prepaid, discount, trial, free
    type: BillingDataType[];
    /** В каком периоде находится сейчас. Или был на момент отмены / завершения */
    period: BillingDataPeriod;
    /** Услуги предоставляемые подпиской */
    services: {
        /** Услуги, которые не относятся к конкретному сервису */
        common?: BillingDataServicesDisableAds;
        // Услуги, которые относятся к Облаку
        cloud?: BillingDataServicesQuota &
            BillingDataServicesGifts &
            BillingDataFreeUpSpace & {
                /** Флаги, выставляемые в UDB */
                flags: BillingDataServicesPaidFlags & BillingDataServicesProFlags;
            };
        /**  Услуги, которые относятся к Почте */
        mail?: BillingDataServicesMail;
        /** Услуги, которые относятся к Заметкам */
        notes?: BillingDataServicesNotes;
    };
    /** Выдавался ли по промокоду */
    promo: boolean;
    /** Предоплачен или нет (здесь нет информации о периодах предоплаты и пр.) */
    prepaid: boolean;
    status:
        | BillingDataStatus.ACTIVE
        | BillingDataStatus.FINISHED
        | BillingDataStatus.GRACE
        | BillingDataStatus.HOLD
        | BillingDataStatus.WAITING;
    grace?: BillingDataWebGrace;
}

export interface BillingDataIos
    extends Omit<BillingDataWeb, 'services' | 'autorenewable' | 'renew_next' | 'status' | 'amounts' | 'intervals'> {
    status: BillingDataStatus.ACTIVE | BillingDataStatus.FINISHED | BillingDataStatus.LAPSED;
    /** Основная стоимость */
    amount: number;
    // Услуги подписки
    services: {
        // Услуги, которые не относятся к конкретному сервису
        common?: BillingDataServicesDisableAds;
        // Услуги, которые относятся к Облаку
        cloud?: BillingDataServicesQuota &
            BillingDataServicesGifts & {
                /** Флаги, выставляемые в UDB */
                flags: BillingDataServicesPaidFlags & BillingDataServicesProFlags;
            };
    };
}

export interface BillingDataReward extends BillingDataHasProduct {
    // ID подписки
    reward_id: string;
    // Дата начала действия подписки в формате timestamp
    start: number;
    // Дата окончания действия подписки в формате timestamp
    finish: number;
    // Услуги предоставляемые подпиской
    services: BillingDataRewardServices;
    // Платформа/партнер, с которой(го) была получена подписка
    platform: BillingDataPlatform | BillingDataPartner;
    // Предоплачена ли подписка
    prepaid?: boolean;
    status: BillingDataStatus.ACTIVE | BillingDataStatus.FINISHED;
    type: (BillingDataType.PAID | BillingDataType.PREPAID)[];
}

interface BillingDataRewardServices {
    // Услуги, которые не относятся к конкретному сервису
    common?: BillingDataServicesDisableAds;
    // Услуги, которые относятся к Облаку
    cloud?: BillingDataServicesQuota &
        BillingDataServicesGifts &
        BillingDataServicesUnlocked & {
            /** Флаги, выставляемые в UDB */
            flags: BillingDataServicesPaidFlags & BillingDataServicesProFlags;
        };
}

export interface BillingDataPromo {
    // ID промо периода
    promo_id: string;
    // ID промо акции
    action: string;
    // Дата начала периода в формате timestamp
    start: number;
    // Дата окончания периода в формате timestamp
    finish: number;
    // Квота добавляемая при активации периода
    quota: number;
    services: BillingDataRewardServices;
}

export interface BillingDataBonus {
    // ID промо периода
    bonus_id: string;
    // ID промо акции
    action: string;
    // Дата начала периода в формате timestamp
    start: number;
    // Дата окончания периода в формате timestamp
    finish: number;
    // Квота добавляемая при активации периода
    quota: number;
    services: BillingDataRewardServices;
}

interface BillingDataGiftible extends BillingDataHasProduct {
    services: BillingDataRewardServices;
}

export interface BillingDataGiftReceived {
    giftible: BillingDataGiftible;
    gift_id: string;
    start: number;
    finish: number;
    create: number;
    platform: string;
    /** Был ли получен получателем */
    received?: boolean;
}

export interface BillingDataGiftSender {
    code: string;
    giftible: BillingDataGiftible;
    gift_id: string;
    start: number;
    finish: number;
    create: number;
    platform: string;
    /** Был ли получен получателем */
    given: boolean;
}

interface BillingDataPurchase extends BillingDataHasProduct {
    payment_id: string;
    product_name: string;
    amount: number;
    ctime: number;
    start: number;
    finish: number;
    refund: number;
    extra: Record<string, any>;
}

export interface BillingPlatformPaidInfo {
    trial: number;
    paid: number;
}

interface BillingPaidInfo {
    cloud?: Record<string, BillingPlatformPaidInfo>;
}

export interface BillingDataReferralProgramResponse {
    // ID обнуса
    referral_id: string;
    // Скрытая почта реферала (u@mail.ru -> u*****@mail.ru, user123456@mail.ru -> user*****@mail.ru)
    referral: string;
    // Дата действия бонуса
    start?: number;
    // Дата окончания бонуса
    finish?: number;
    // Имеет ли автопродление (только для бонусов от web подписок)
    autorenewable?: boolean;
    // Платформа подписки (web, android, ios, rustore, huawei)
    type: BillingDataPlatform;
    // Тип бонуса ('default' - 200 ГБ квоты)
    bonus: string;
    // Услуги бонуса
    services?: {
        // Облачные услуги
        cloud?: BillingDataServicesQuota;
    };
}
